import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Dot } from '@givebutter/primitives/dot';
import { useToast } from '@givebutter/primitives/toast';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cancelCheckInTicket, checkInTicket } from 'api/tickets';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { eventQueryKeys } from 'queries/event-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { Ticket } from 'types/domain';

interface ToggleTicketCheckInButtonProps {
    ticket: Ticket;
}

const ToggleTicketCheckInButton = (props: ToggleTicketCheckInButtonProps) => {
    const { ticket } = props;
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const queryClient = useQueryClient();
    const isCheckedIn = !!ticket.checked_in_at;

    const { mutate: checkIn } = useMutation({
        mutationFn: (ticketId: number) => checkInTicket(ticketId),
        onSuccess: () => {
            success('Checked-in successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tickets(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.bundlePurchases(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: eventQueryKeys.eventById(ticket.event_id),
            });
        },
        onError: () => {
            error('Could not check-in ticket. Please try again.');
        },
    });

    const { mutate: cancelCheckIn } = useMutation({
        mutationFn: (ticketId: number) => cancelCheckInTicket(ticketId),
        onSuccess: () => {
            success('Cancelled check-in successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tickets(accountId) });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.bundlePurchases(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: eventQueryKeys.eventById(ticket.event_id),
            });
        },
        onError: () => {
            error('Could not cancel check-in. Please try again.');
        },
    });

    if (ticket?.revoked_at) {
        return (
            <div className="flex items-center gap-x-2">
                <Dot className={'bg-red-500'} />
                <span className="flex">Revoked</span>
            </div>
        );
    }

    return (
        <Button
            size={'sm'}
            variant={'outline'}
            color={'neutral'}
            className="flex items-center gap-2"
            onClick={() => (isCheckedIn ? cancelCheckIn(ticket.id) : checkIn(ticket.id))}
        >
            <span className="font-medium">{isCheckedIn ? 'Checked in' : 'Check in'}</span>
            <FontAwesomeIcon
                icon={isCheckedIn ? faCheckCircle : faCircle}
                className={`${isCheckedIn ? 'text-green-500' : 'text-gray-300'}`}
            />
        </Button>
    );
};

export { ToggleTicketCheckInButton };
